import {Component, DOCUMENT, HostListener, Inject, NgZone, OnDestroy, OnInit, Renderer2} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {Observable, Subject, Subscription} from "rxjs";
import {AddHomescreenService} from "./pwa/add-homescreen.service";
import {DEFAULT_INTERRUPTSOURCES, Idle} from "@ng-idle/core";
import {GeneralRequestService} from "./services/general-request.service";
import {NotificationService} from "./services/notification.service";
import {NavigationEnd, Router, RouterModule, RouterOutlet} from "@angular/router";
import {filter, takeUntil} from "rxjs/operators";
import {DateAdapter} from "@angular/material/core";
import {environment} from "src/environments/environment";
import {NgClass} from "@angular/common";
import {AuthService} from "./services/auth.service";
import {ModalService} from "./services/modal.service";
import {PageTitleService} from "./services/page-title.service";
import {UrlService} from "./services/url.service";
import {AccessibilityService} from "./shared/accessibility/accessibility.service";
import {AppConfigService} from "./services/app-config.service";
import {FabButtonComponent} from "./shared/fab-button-service/fab-button.component";
import {GlobalHeaderComponent} from "./features/global-header/global-header.component";
import {SkipLinkComponent} from "./shared/skip-link/skip-link.component";
import {IconService} from "./services/icon.service";
import {PremiumServicesService} from "./services/premium-services.service";
import {EmailValidationService} from "./services/email-validation.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.css"],
  imports: [
    SkipLinkComponent,
    GlobalHeaderComponent,
    FabButtonComponent,
    RouterOutlet,
    RouterModule,
    NgClass,
  ]
})
export class AppComponent implements OnInit, OnDestroy {

  readonly MAIN_PAGE_ROUTES = [ '/', '/premium-services'];
  readonly DEFAULT_LANG = "et";
  readonly LEGACY_LANG_CODE = 'ee';
  readonly UPDATED_KEY = 'Updated';
  readonly LANG_KEY = 'lang';

  sessionInitialized$: Observable<boolean>;
  isNew: boolean;
  showSkipLink: boolean;
  title = "egp";
  idleState = "Not started.";
  fetchNewMessagesInterval: ReturnType<typeof setInterval>;
  currentUrl: string;
  isMainPage = false;

  private destroy$ = new Subject<void>();

  constructor(
    public translate: TranslateService,
    public authService: AuthService,
    public addHomescreenService: AddHomescreenService,
    private idle: Idle,
    private modalService: ModalService,
    private accessibility: AccessibilityService,
    private generalRequestService: GeneralRequestService,
    private notificationService: NotificationService,
    private ngZone: NgZone,
    private router: Router,
    private renderer: Renderer2,
    private pageTitleService: PageTitleService,
    private urlService: UrlService,
    private dateAdapter: DateAdapter<Date>,
    private configService: AppConfigService,
    private iconService: IconService,
    private premiumServices: PremiumServicesService,
    private emailValidationService: EmailValidationService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.initializeServices();
  }

  ngOnInit() {
    this.initializeApp();
    this.setupSubscriptions();
    this.setupIdleHandling();
    this.setupMessageFetching();

    // Setup message fetching
    this.sessionInitialized$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        if (isLoggedIn && !this.isNew) {
          this.resetIdle();
          this.setUpMessageFetching();
        } else {
          clearInterval(this.fetchNewMessagesInterval);
          this.idle.stop();
        }
      });
  }

  private initializeServices(): void {
    this.iconService.registerIcons();
    this.configService.loadConfig();
    this.sessionInitialized$ = this.authService.isSessionInitialized$();
    this.translate.setDefaultLang(this.DEFAULT_LANG);
    this.pageTitleService.initialize();
  }

  private initializeApp(): void {
    this.premiumServices.fetchPremiumStatus();
    this.setLang();
    this.checkIfUpdated();
    this.emailValidationService.subscribeToEmailValidation(); }

  private setupSubscriptions(): void {
    this.setupAccessibilitySubscription();
    this.setupAuthSubscription();
    this.setupRouterSubscription();
  }

  private setupAccessibilitySubscription(): void {
    this.accessibility.showSkipLink$
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.showSkipLink = value);
  }

  private setupAuthSubscription(): void {
    this.authService.isNew$
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.isNew = value);
  }

  private setupRouterSubscription(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$))
      .subscribe((event: NavigationEnd) => {
        this.handleNavigationEnd(event);
      });
  }

  private handleNavigationEnd(event: NavigationEnd): void {
    this.urlService.setPreviousUrl(this.currentUrl);
    this.currentUrl = event.url;
    this.urlService.setCurrentUrl(this.currentUrl);
    this.isMainPage = this.MAIN_PAGE_ROUTES.includes(this.currentUrl as any);
    this.resetFocus();
  }

  private setupIdleHandling(): void {
    this.idle.onIdleEnd
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.resetIdle());

    this.idle.onIdleStart
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.modalService.showIdleModal(this.idleState));

    this.idle.onTimeout
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.handleIdleTimeout());
  }

  private setupMessageFetching(): void {
    this.sessionInitialized$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        if (isLoggedIn && !this.isNew) {
          this.resetIdle(); this.setUpMessageFetching();
        } else {
          this.clearMessageInterval();
          this.idle.stop();
        }
      });
  }

  private handleIdleTimeout(): void {
    this.idle.stop();
    this.clearMessageInterval();
    this.authService.clearSession();
  }

  resetIdle() {
    this.idle.stop();
    this.idle.setIdle(environment.session.sessionLength);
    this.idle.setTimeout(environment.session.idleLength);
    // sessionLength sets a timeout period. after that, when <idleLength> seconds of inactivity have past, the user will be considered timed out.
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.watch();
  }

  setUpMessageFetching() {
    this.generalRequestService.fetchMeetings();
    this.notificationService.fetchNotificationsOnInit();
    this.ngZone.runOutsideAngular(() => {
      this.fetchNewMessagesInterval = setInterval(() => {
        this.ngZone.run(() => {
          this.generalRequestService.fetchMeetings(true);
          this.notificationService.fetchNewNotifications(true);
        });
      }, (environment.session.messageFetchFrequency * 1000));
    });
  }

  resetFocus() {
    // Set focus to an element at the top of the page for accessibility
    const topElement = this.renderer.selectRootElement("#top-of-page");
    if (topElement) {
      topElement.focus();
    }
  }

  setLang() {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(changeEvent => {
        localStorage.setItem(this.LANG_KEY, changeEvent.lang);
        sessionStorage.setItem(this.LANG_KEY, changeEvent.lang);
        this.dateAdapter.setLocale(changeEvent.lang);
        this.document.documentElement.lang = changeEvent.lang;
      });

    const storedLang = localStorage.getItem(this.LANG_KEY);
    if (storedLang === this.LEGACY_LANG_CODE) localStorage.setItem(this.LANG_KEY, this.DEFAULT_LANG);

    if (localStorage.getItem(this.LANG_KEY)) {
      this.translate.use(localStorage.getItem(this.LANG_KEY));
    } else {
      this.translate.use(this.translate.defaultLang);
      localStorage.setItem(this.LANG_KEY, this.translate.currentLang);
    }
  }

  private checkIfUpdated() {
    const updated = localStorage.getItem(this.UPDATED_KEY);
    if (updated === 'true') {
      this.modalService.showNewVersionDialog('landing.versionUpdate');
      localStorage.removeItem(this.UPDATED_KEY);
    }
  }

  private clearMessageInterval() {
    clearInterval(this.fetchNewMessagesInterval);
  }

  ngOnDestroy() {
    this.clearMessageInterval();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener("window:beforeinstallprompt", ["$event"])
  onbeforeinstallprompt(e: Event) {
    // Prevent Chrome 67 and earlier from automatically showing the prompt
    e.preventDefault();
    // Stash the event so it can be triggered later.
    this.addHomescreenService.storePrompt(e);
  }
}
